import { ApiConfig, GlobalSettingsUpdate, CustomerSettingsUpdate } from '../types/types';
import { apiFetch, buildAuthHeaders, SessionExpiredError } from '../auth/apiFetch';
import { fetchMetricsText } from '../auth/metricsFetch';

const API_BASE_URL = '';

export { SessionExpiredError };

class ApiService {
  private static instance: ApiService;
  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private apiHeaders(): Record<string, string> {
    return buildAuthHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
  }

  async getExtendedConfig(): Promise<ApiConfig> {
    const response = await apiFetch(`${API_BASE_URL}/getextendedconfig`, {
      headers: this.apiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch extended configuration: ${response.status}`);
    }

    const data = await response.json();

    return {
      globalSettings: {
        globalReadBatchSize: data.globalReadBatchSize,
        globalReadDelayMillis: data.globalReadDelayMillis,
        globalPostURL: data.globalPostURL,
        logLevel: data.logLevel,
        artificialDelaySetting: data.artificialDelaySetting,
      },
      customers: data.customers,
      appVersion: data.appVersion,
    };
  }

  async removeCustomer(apikey: string): Promise<void> {
    const response = await apiFetch(`${API_BASE_URL}/removecustomer`, {
      method: 'POST',
      headers: this.apiHeaders(),
      body: JSON.stringify({ apikey }),
    });

    if (!response.ok) {
      throw new Error(`Failed to remove customer: ${response.status}`);
    }
  }

  async flushQueue(apikey: string, dateUntil: string): Promise<any> {
    const response = await apiFetch(`${API_BASE_URL}/deletemessages`, {
      method: 'POST',
      headers: this.apiHeaders(),
      body: JSON.stringify({ apikey, dateUntil }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as { error?: string }).error || 'Failed to flush queue');
    }

    return response.json();
  }

  async getRocksDBStats(): Promise<any> {
    const response = await apiFetch(`${API_BASE_URL}/rocksdbstats`, {
      headers: this.apiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get RocksDB stats: ${response.status}`);
    }

    return response.json();
  }

  async triggerCompaction(): Promise<void> {
    const response = await apiFetch(`${API_BASE_URL}/compact`, {
      method: 'POST',
      headers: this.apiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger compaction: ${response.status}`);
    }
  }

  async getSyncStatus(): Promise<{
    role: string;
    syncEnabled: boolean;
    synced: boolean;
    lastError?: string | null;
    lastAttemptAt?: number | null;
    lastSuccessAt?: number | null;
    configVersion?: number;
    usersVersion?: number;
    peerConfigVersion?: number | null;
    peerUsersVersion?: number | null;
    secondarySyncUrl?: string | null;
  }> {
    const response = await apiFetch(`${API_BASE_URL}/sync-status`, {
      headers: this.apiHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch sync status: ${response.status}`);
    }
    return response.json();
  }

  async postConfig(config: {
    globalSettings?: GlobalSettingsUpdate;
    customers?: CustomerSettingsUpdate[];
  }): Promise<void> {
    const transformedConfig = {
      ...config.globalSettings,
      customers: config.customers,
    };

    const response = await apiFetch(`${API_BASE_URL}/postconfig`, {
      method: 'POST',
      headers: this.apiHeaders(),
      body: JSON.stringify(transformedConfig),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update configuration: ${response.status} - ${errorText}`);
    }
  }

  async fetchMetrics(): Promise<string> {
    return fetchMetricsText();
  }

  async fetchMessages(apiKey: string, startKey?: string, limit?: string): Promise<any> {
    let url = `${API_BASE_URL}/messages?apikey=${apiKey}`;
    if (startKey) url += `&startKey=${startKey}`;
    if (limit) url += `&limit=${limit}`;

    const response = await apiFetch(url, { headers: this.apiHeaders() });

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }

    return response.json();
  }
}

export const apiService = ApiService.getInstance();
